import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { ChevronDown, ChevronUp, ChevronsDownUp, ChevronsUpDown, Plus, X } from "lucide-react";
import { cn } from "@nexus/ui";
import type {
  CreateUserKpiBody,
  UpdateUserKpiBody,
  UserKpiEntry,
} from "@nexus/contracts";
import { useKpiSectionLayout } from "../hooks/useKpiSectionLayout";
import type { UserKpiScope } from "../api/user-kpis.api";
import { getKpiGroupId, groupUserKpis, type GroupedKpis, type KpiGroupId } from "../lib/kpi-groups";
import { reorderUserKpiNames } from "../lib/user-kpi-model";
import { KpiSettingsDialog, type KpiSettingsDialogValue } from "./KpiSettingsDialog";
import { UserKpiBox, type UserKpiBoxLayout, type UserKpiTone } from "./UserKpiBox";

/** Per-KPI display values the owning feature computes (badge logic stays theirs). */
export interface UserKpiCellValue {
  value: string;
  badgeText: string | null;
  tone: UserKpiTone;
}

export interface UserKpiGridProps {
  /** Page domain whose permission gates the section-layout routes. */
  scope: UserKpiScope;
  entries: UserKpiEntry[];
  /** Keyed by `UserKpiEntry.name`. A missing entry renders the empty placeholder. */
  valuesByName: Record<string, UserKpiCellValue | undefined>;
  revealHidden?: boolean;
  isLoading?: boolean;
  emptyValue?: string;
  className?: string;
  boxLayout?: UserKpiBoxLayout;
  onReorder?: (names: string[]) => void;
  onCreate?: (body: CreateUserKpiBody) => void | Promise<void>;
  onUpdate?: (id: number, body: UpdateUserKpiBody) => void;
  isSubmitting?: boolean;
  onToggleHidden?: (entry: UserKpiEntry) => void;
}

export function UserKpiGrid({
  scope,
  entries,
  valuesByName,
  revealHidden = false,
  isLoading = false,
  emptyValue = "—",
  className,
  boxLayout = "default",
  onReorder,
  onCreate,
  onUpdate,
  isSubmitting = false,
  onToggleHidden,
}: UserKpiGridProps) {
  const { t } = useTranslation();
  const { layout, moveSection, toggleCollapsed, removeSection, restoreSections } =
    useKpiSectionLayout(scope);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<UserKpiEntry | null>(null);

  // A click that does not travel 5px is a click, not a drag — otherwise the
  // PointerSensor swallows the edit and hide buttons inside each box.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const rendered = useMemo(
    () => (revealHidden ? entries : entries.filter((entry) => !entry.hidden)),
    [entries, revealHidden],
  );
  const removed = useMemo(() => new Set(layout.removed), [layout.removed]);
  const groups = useMemo(
    () =>
      groupUserKpis(rendered, { order: layout.order })
        .filter((group) => !removed.has(group.id))
        .filter((group) => isVisibleKpiGroup(group, valuesByName, emptyValue, isLoading)),
    [rendered, layout.order, removed, valuesByName, emptyValue, isLoading],
  );
  const visibleIds = useMemo(() => groups.map((group) => group.id), [groups]);
  const offeredSectionIds = useMemo(
    () => layout.order.filter((id) => !removed.has(id)),
    [layout.order, removed],
  );

  function groupOf(name: string): KpiGroupId {
    return getKpiGroupId(rendered.find((entry) => entry.name === name) ?? { name });
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorder) return;
    const activeName = String(active.id);
    const overName = String(over.id);
    // A drop into another section is ignored. Section order has its own controls.
    if (groupOf(activeName) !== groupOf(overName)) return;
    onReorder(reorderUserKpiNames(entries, activeName, overName));
  }

  async function handleSubmit(value: KpiSettingsDialogValue) {
    try {
      if (editing && onUpdate) {
        onUpdate(editing.id, {
          displayName: value.displayName,
          formula: value.formula,
          format: value.format,
          polarity: value.polarity,
          decimals: value.decimals,
          colorToken: value.colorToken,
        });
      } else if (onCreate) {
        await onCreate({
          displayName: value.displayName,
          formula: value.formula,
          format: value.format,
          polarity: value.polarity,
          decimals: value.decimals,
          colorToken: value.colorToken,
          hidden: false,
          shareWithOrg: value.shareWithOrg,
          ...(value.section ? { section: value.section } : {}),
        });
      }
      setDialogOpen(false);
      setEditing(null);
    } catch {
      // The mutation already reports the failure.
    }
  }

  return (
    <>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div className="flex flex-col gap-6">
          {layout.removed.length > 0 && (
            <button
              type="button"
              className="self-start text-xs font-medium text-muted-foreground hover:text-foreground"
              onClick={restoreSections}
            >
              {t(
                layout.removed.length === 1
                  ? "kpiSettings.groups.showRemoved_one"
                  : "kpiSettings.groups.showRemoved_other",
                layout.removed.length === 1
                  ? "Show removed section ({{count}})"
                  : "Show removed sections ({{count}})",
                { count: layout.removed.length },
              )}
            </button>
          )}

          {groups.map((group, index) => {
            const label = t(group.labelKey, group.defaultLabel);
            const isCollapsed = layout.collapsed.includes(group.id);
            return (
              <section key={group.id} data-testid={`kpi-group-${group.id}`}>
                <div className="mb-3 flex items-center gap-1">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {label}
                  </p>
                  <div className="ml-auto flex items-center gap-0.5">
                    <SectionIconButton
                      label={t("kpiSettings.groups.moveUp", "Move {{name}} up", { name: label })}
                      disabled={index === 0}
                      onClick={() => moveSection(group.id, "up", visibleIds)}
                    >
                      <ChevronUp className="size-3.5" aria-hidden />
                    </SectionIconButton>
                    <SectionIconButton
                      label={t("kpiSettings.groups.moveDown", "Move {{name}} down", { name: label })}
                      disabled={index === groups.length - 1}
                      onClick={() => moveSection(group.id, "down", visibleIds)}
                    >
                      <ChevronDown className="size-3.5" aria-hidden />
                    </SectionIconButton>
                    <SectionIconButton
                      label={
                        isCollapsed
                          ? t("kpiSettings.groups.expand", "Expand {{name}}", { name: label })
                          : t("kpiSettings.groups.collapse", "Collapse {{name}}", { name: label })
                      }
                      onClick={() => toggleCollapsed(group.id)}
                    >
                      {isCollapsed ? (
                        <ChevronsUpDown className="size-3.5" aria-hidden />
                      ) : (
                        <ChevronsDownUp className="size-3.5" aria-hidden />
                      )}
                    </SectionIconButton>
                    <SectionIconButton
                      label={t("kpiSettings.groups.remove", "Remove {{name}}", { name: label })}
                      onClick={() => removeSection(group.id)}
                    >
                      <X className="size-3.5" aria-hidden />
                    </SectionIconButton>
                  </div>
                </div>

                {isCollapsed ? null : (
                  <SortableContext
                    items={group.entries.map((entry) => entry.name)}
                    strategy={rectSortingStrategy}
                  >
                    <div className={cn("grid grid-cols-2 gap-3 sm:grid-cols-3", className)}>
                      {group.entries.map((entry) => {
                        const cell = valuesByName[entry.name];
                        return (
                          <UserKpiBox
                            key={entry.name}
                            entry={entry}
                            layout={boxLayout}
                            value={cell?.value ?? emptyValue}
                            badgeText={cell?.badgeText ?? null}
                            tone={cell?.tone ?? "neutral"}
                            isLoading={isLoading}
                            draggable={!!onReorder}
                            onEdit={
                              onUpdate
                                ? (target) => {
                                    setEditing(target);
                                    setDialogOpen(true);
                                  }
                                : undefined
                            }
                            onToggleHidden={onToggleHidden}
                          />
                        );
                      })}
                    </div>
                  </SortableContext>
                )}
              </section>
            );
          })}

          {onCreate && (
            <div className={cn("grid grid-cols-2 gap-3 sm:grid-cols-3", className)}>
              <button
                type="button"
                className="flex min-h-[72px] items-center justify-center gap-1.5 rounded-lg border border-dashed border-border text-xs font-medium text-muted-foreground hover:border-ring hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                onClick={() => {
                  setEditing(null);
                  setDialogOpen(true);
                }}
              >
                <Plus className="size-3.5" aria-hidden />
                {t("kpiSettings.addBox", "Add KPI")}
              </button>
            </div>
          )}
        </div>
      </DndContext>

      {(onCreate || onUpdate) && (
        <KpiSettingsDialog
          open={dialogOpen}
          onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) setEditing(null);
          }}
          mode={editing ? "edit" : "create"}
          scope="user"
          offeredSectionIds={offeredSectionIds}
          isSubmitting={isSubmitting}
          initial={
            editing
              ? {
                  displayName: editing.displayName,
                  formula: editing.formula ?? editing.name,
                  format: editing.format,
                  polarity: editing.polarity,
                  decimals: editing.decimals,
                  colorToken: editing.colorToken,
                }
              : undefined
          }
          onSubmit={handleSubmit}
        />
      )}
    </>
  );
}

/**
 * A section with no rendered KPIs is omitted. While loading, a section that
 * still has rows stays visible so the skeletons have a heading. Once loaded,
 * a section whose cells are all missing or the empty placeholder is hidden.
 */
function SectionIconButton({
  label,
  disabled = false,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className="flex size-5 items-center justify-center rounded text-muted-foreground/70 hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function isVisibleKpiGroup(
  group: GroupedKpis<UserKpiEntry>,
  valuesByName: Record<string, UserKpiCellValue | undefined>,
  emptyValue: string,
  isLoading: boolean,
): boolean {
  if (group.entries.length === 0) return false;
  if (isLoading) return true;
  return group.entries.some((entry) => {
    const cell = valuesByName[entry.name];
    return cell != null && cell.value !== emptyValue;
  });
}
